// Clinical Pathology — sample handling + haematology/biochemistry interpretation.
// Purely client-side, single-session. Pitched at Cert IV Veterinary Nursing.
//
// Five staged beats per case (budget = 100):
//   1. Sample handling / tube selection   (choice · 20)
//   2. Pre-analytical QC                   (tap problems · 20)
//   3. Haematology interpretation          (panel + choice · 20)
//   4. Biochemistry interpretation         (panel + choice · 20)
//   5. Action / report                     (choice · 20)
//
// Reference ranges are typical small-animal SI values used for teaching; real
// ranges vary by laboratory and analyser.

import { optPts, gradeChecklist } from "@/data/admission";

export { optPts, gradeChecklist };

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const serveCase = (c) => ({
  ...c,
  handling: { ...c.handling, options: shuffle(c.handling.options) },
  qc: { ...c.qc, checklist: shuffle(c.qc.checklist) },
  haem: { ...c.haem, options: shuffle(c.haem.options) },
  biochem: { ...c.biochem, options: shuffle(c.biochem.options) },
  action: { ...c.action, options: shuffle(c.action.options) },
});

export const CLINPATH_CASES = [
  {
    id: "rusty-ckd",
    name: "Rusty",
    species: "dog",
    breed: "Border Collie",
    signalment: "10 yr · male neutered",
    presentation: "PU/PD, weight loss and a poor appetite over several weeks.",
    requested: "CBC + biochemistry + electrolytes",
    handling: {
      prompt: "What tubes and handling do you select for the requested tests?",
      options: [
        { t: "EDTA (lavender) for the CBC — invert gently to mix — plus a serum/gel tube for biochemistry; spin and separate the serum promptly.", score: 3, note: "Correct: EDTA preserves cells for haematology; serum is the standard for biochemistry, separated quickly to avoid cell-leakage artefact." },
        { t: "EDTA for everything — the lab can run biochemistry from EDTA plasma.", score: 1, note: "EDTA contaminates potassium and calcium results and isn't ideal for biochemistry." },
        { t: "Serum tube for both tests; shake it to speed up clotting.", score: 0, note: "Serum can't be used for a CBC, and shaking causes haemolysis." },
        { t: "Fluoride-oxalate (grey) tubes for all samples.", score: 0, note: "Fluoride oxalate is for glucose preservation, not general haematology/biochemistry." },
      ],
    },
    qc: {
      prompt: "Inspect the samples as received. Tap every pre-analytical problem you should flag.",
      checklist: [
        { t: "Serum was separated 6 h after collection (kept at room temperature)", flag: true, coach: "Delayed separation falsely raises potassium (cell leakage) and lowers glucose (glycolysis)." },
        { t: "EDTA tube only half-filled", flag: true, coach: "Excess EDTA relative to blood shrinks red cells and skews indices — fill to the line." },
        { t: "Potassium flagged high — but the serum sat for hours", flag: true, coach: "The high potassium may be artefactual from the delay; confirm on a fresh, promptly-separated sample." },
        { t: "Tube labelled with patient name, date and time", flag: false, coach: "" },
        { t: "Serum is clear and straw-coloured", flag: false, coach: "" },
        { t: "EDTA gently inverted to mix", flag: false, coach: "" },
        { t: "No lipaemia noted", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology panel and choose the best interpretation.",
      panel: [
        { a: "PCV", v: "31", u: "%", ref: "37–55", flag: "low" },
        { a: "RBC", v: "4.6", u: "×10¹²/L", ref: "5.5–8.5", flag: "low" },
        { a: "Haemoglobin", v: "105", u: "g/L", ref: "120–180", flag: "low" },
        { a: "Reticulocytes", v: "18", u: "×10⁹/L", ref: ">60 if regenerating", flag: "low" },
        { a: "WBC", v: "9.2", u: "×10⁹/L", ref: "6.0–17.0", flag: null },
        { a: "Platelets", v: "320", u: "×10⁹/L", ref: "200–500", flag: null },
      ],
      options: [
        { t: "Mild non-regenerative anaemia — consistent with anaemia of chronic kidney disease (reduced erythropoietin).", score: 3, note: "Low PCV with an inadequate reticulocyte response = non-regenerative; fits CKD." },
        { t: "Mild anaemia — recommend a reticulocyte count to assess regeneration.", score: 1, note: "Reasonable, but the panel already shows reticulocytes are inappropriately low." },
        { t: "Regenerative anaemia caused by blood loss.", score: 0, note: "Reticulocytes are low, so this is not regenerative." },
        { t: "Normal haematology.", score: 0, note: "PCV, RBC and Hb are all below range." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry panel and choose the best interpretation.",
      panel: [
        { a: "Urea", v: "38", u: "mmol/L", ref: "3.5–9.0", flag: "high" },
        { a: "Creatinine", v: "410", u: "µmol/L", ref: "40–130", flag: "high" },
        { a: "Phosphate", v: "2.8", u: "mmol/L", ref: "0.8–1.6", flag: "high" },
        { a: "Potassium", v: "5.9", u: "mmol/L", ref: "3.5–5.8", flag: "high" },
        { a: "Albumin", v: "30", u: "g/L", ref: "26–40", flag: null },
        { a: "Glucose", v: "5.1", u: "mmol/L", ref: "3.3–6.5", flag: null },
      ],
      options: [
        { t: "Marked azotaemia with hyperphosphataemia — consistent with renal disease; correlate with urine specific gravity.", score: 3, note: "High urea + creatinine + phosphate in a PU/PD patient points to renal azotaemia." },
        { t: "Azotaemia — could be pre-renal; check hydration and USG.", score: 1, note: "Worth checking, but with PU/PD, hyperphosphataemia and non-regenerative anaemia, renal is most likely." },
        { t: "Primary liver disease.", score: 0, note: "Liver enzymes/bilirubin aren't the abnormality here." },
        { t: "Results within normal limits.", score: 0, note: "Urea, creatinine and phosphate are clearly elevated." },
      ],
    },
    action: {
      prompt: "What's the best next step?",
      options: [
        { t: "Report the abnormalities to the vet; recommend a fresh, correctly-handled repeat for potassium and obtain urine for USG.", score: 3, note: "Reports findings, addresses the delayed-sample artefact, and gathers the USG needed to confirm renal origin." },
        { t: "Flag the results to the vet.", score: 1, note: "Right direction, but misses the potassium artefact and the USG." },
        { t: "File the results as normal.", score: 0, note: "They are markedly abnormal." },
        { t: "Tell the owner the dog is in kidney failure.", score: 0, note: "Interpreting/communicating a diagnosis to the owner is the vet's role." },
      ],
    },
  },

  {
    id: "smudge-diabetes",
    name: "Smudge",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "9 yr · male neutered",
    presentation: "PU/PD and weight loss despite a good appetite.",
    requested: "Glucose + biochemistry + CBC",
    handling: {
      prompt: "Which tubes/handling give a reliable glucose and biochemistry?",
      options: [
        { t: "Fluoride-oxalate (grey) tube for glucose to inhibit glycolysis; serum/gel for the rest of the biochemistry; EDTA for the CBC.", score: 3, note: "Fluoride oxalate stops red cells consuming glucose, preventing a falsely low result." },
        { t: "Serum for glucose, but spin and separate it immediately to limit glycolysis.", score: 1, note: "Acceptable if separated at once, but a fluoride tube is the safer choice." },
        { t: "EDTA for the glucose sample.", score: 0, note: "EDTA doesn't prevent glycolysis; glucose will fall over time." },
        { t: "Leave whole blood at room temperature and run the glucose in the morning.", score: 0, note: "Glycolysis will markedly lower the glucose." },
      ],
    },
    qc: {
      prompt: "Tap every pre-analytical concern that could affect interpretation.",
      checklist: [
        { t: "Glucose collected into a plain serum tube and separated 4 h later", flag: true, coach: "Glycolysis falsely lowers glucose — use fluoride oxalate or separate immediately." },
        { t: "The cat was stressed and struggling during collection", flag: true, coach: "Stress hyperglycaemia is common in cats — interpret a single high glucose with caution and consider fructosamine." },
        { t: "Fructosamine not requested to confirm persistent hyperglycaemia", flag: true, coach: "Fructosamine reflects average glucose over weeks and distinguishes diabetes from stress hyperglycaemia." },
        { t: "Grey-top tube gently inverted to mix", flag: false, coach: "" },
        { t: "Samples labelled correctly", flag: false, coach: "" },
        { t: "Serum is clear, not lipaemic", flag: false, coach: "" },
        { t: "No clots in the EDTA", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "19.5", u: "×10⁹/L", ref: "5.5–19.5", flag: "high" },
        { a: "Neutrophils", v: "14.0", u: "×10⁹/L", ref: "2.5–12.5", flag: "high" },
        { a: "Lymphocytes", v: "1.0", u: "×10⁹/L", ref: "1.5–7.0", flag: "low" },
        { a: "Eosinophils", v: "0.2", u: "×10⁹/L", ref: "0.1–1.5", flag: null },
        { a: "PCV", v: "38", u: "%", ref: "30–45", flag: null },
      ],
      options: [
        { t: "Stress (steroid) leukogram — mature neutrophilia with lymphopenia; common in a stressed cat.", score: 3, note: "Neutrophilia + lymphopenia without a left shift is the classic stress pattern." },
        { t: "Inflammatory leukogram with a left shift.", score: 1, note: "No band neutrophils are reported, so this is more stress than active inflammation." },
        { t: "Leukaemia.", score: 0, note: "These mild changes don't indicate neoplasia." },
        { t: "Normal haematology.", score: 0, note: "There is a neutrophilia with lymphopenia." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Glucose", v: "22.0", u: "mmol/L", ref: "3.3–6.5", flag: "high" },
        { a: "Cholesterol", v: "8.1", u: "mmol/L", ref: "2.0–6.0", flag: "high" },
        { a: "ALT", v: "95", u: "U/L", ref: "10–100", flag: null },
        { a: "Urea", v: "7.0", u: "mmol/L", ref: "5.0–11.0", flag: null },
        { a: "Potassium", v: "3.4", u: "mmol/L", ref: "3.5–5.5", flag: "low" },
      ],
      options: [
        { t: "Marked hyperglycaemia — consistent with diabetes mellitus; recommend fructosamine to differentiate from stress hyperglycaemia in a cat.", score: 3, note: "A glucose of 22 with PU/PD strongly suggests diabetes, but fructosamine confirms it in cats." },
        { t: "Hyperglycaemia — purely stress, can be ignored.", score: 0, note: "22 mmol/L is too high to dismiss as stress without confirmation." },
        { t: "Hypoglycaemia.", score: 0, note: "Glucose is high, not low." },
        { t: "Normal glucose.", score: 0, note: "Glucose is markedly elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet; recommend fructosamine ± urine glucose/ketones and a correctly-handled fasting glucose.", score: 3, note: "Confirms persistent hyperglycaemia and screens for ketones before treatment decisions." },
        { t: "Flag the high glucose to the vet.", score: 1, note: "Good, but the fructosamine/urine work-up makes the report far more useful." },
        { t: "Repeat the glucose in a month.", score: 0, note: "A symptomatic patient needs prompt confirmation, not a month's delay." },
        { t: "Advise the owner to start insulin.", score: 0, note: "Starting insulin is a veterinary decision after confirmation." },
      ],
    },
  },

  {
    id: "bella-imha",
    name: "Bella",
    species: "dog",
    breed: "Cocker Spaniel",
    signalment: "4 yr · female spayed",
    presentation: "Sudden lethargy, very pale gums and dark urine.",
    requested: "CBC + blood smear + biochemistry",
    handling: {
      prompt: "How do you collect and prepare the samples?",
      options: [
        { t: "EDTA for the CBC (invert gently) and make a FRESH blood smear at the time of collection; serum for biochemistry.", score: 3, note: "Fresh smears avoid storage artefact and best show spherocytes/agglutination." },
        { t: "EDTA for the CBC — the lab can make a smear later from the EDTA.", score: 1, note: "Storage in EDTA causes morphological artefact; smears are best made fresh." },
        { t: "Lithium-heparin tube for the CBC.", score: 0, note: "Heparin distorts cell staining; EDTA is the haematology tube." },
        { t: "Refrigerate whole blood and make the smear tomorrow.", score: 0, note: "Delayed, refrigerated smears are poor quality." },
      ],
    },
    qc: {
      prompt: "Tap every problem/observation you should flag.",
      checklist: [
        { t: "A visible clot is present in the EDTA tube", flag: true, coach: "A clot invalidates the platelet count and CBC — recollect." },
        { t: "Smear made 24 h later from refrigerated EDTA", flag: true, coach: "Storage artefact (echinocytes/refractile changes) can mimic or mask pathology." },
        { t: "Blood autoagglutinates when mixed with saline on a slide", flag: true, coach: "A positive saline agglutination test supports immune-mediated haemolysis — a key finding to report." },
        { t: "Tube filled to the line and inverted", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
        { t: "PCV spun and read promptly", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "PCV", v: "19", u: "%", ref: "37–55", flag: "low" },
        { a: "RBC", v: "2.8", u: "×10¹²/L", ref: "5.5–8.5", flag: "low" },
        { a: "Haemoglobin", v: "70", u: "g/L", ref: "120–180", flag: "low" },
        { a: "Reticulocytes", v: "185", u: "×10⁹/L", ref: ">60 = regenerative", flag: "high" },
        { a: "Spherocytes", v: "Present", u: "", ref: "absent", flag: "high" },
        { a: "Platelets", v: "210", u: "×10⁹/L", ref: "200–500", flag: null },
      ],
      options: [
        { t: "Marked regenerative anaemia with spherocytes and agglutination — consistent with immune-mediated haemolytic anaemia (IMHA).", score: 3, note: "Strong reticulocytosis + spherocytes + agglutination is the classic IMHA picture." },
        { t: "Regenerative anaemia — most likely blood loss.", score: 1, note: "It is regenerative, but spherocytes/agglutination point to haemolysis, not loss." },
        { t: "Non-regenerative anaemia of chronic disease.", score: 0, note: "Reticulocytes are high, so it is regenerative." },
        { t: "Normal haematology.", score: 0, note: "PCV is severely low." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Total bilirubin", v: "45", u: "µmol/L", ref: "0–15", flag: "high" },
        { a: "ALT", v: "80", u: "U/L", ref: "10–100", flag: null },
        { a: "Total protein", v: "60", u: "g/L", ref: "54–75", flag: null },
        { a: "Urea", v: "6.0", u: "mmol/L", ref: "3.5–9.0", flag: null },
      ],
      options: [
        { t: "Hyperbilirubinaemia consistent with haemolysis.", score: 3, note: "Destroyed red cells release bilirubin, raising it without major liver-enzyme change." },
        { t: "Primary liver disease.", score: 1, note: "Possible, but ALT is normal and the haemolysis explains the bilirubin." },
        { t: "Renal disease.", score: 0, note: "Urea is normal." },
        { t: "Normal biochemistry.", score: 0, note: "Bilirubin is clearly elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Treat as urgent — alert the vet immediately (severe anaemia); recommend a fresh EDTA, a new smear and a saline agglutination test.", score: 3, note: "A PCV of 19% with suspected IMHA is an emergency and the clotted sample must be replaced." },
        { t: "Flag the results to the vet.", score: 1, note: "Right, but the severity warrants emphasising urgency and recollecting the clotted tube." },
        { t: "File and recheck next week.", score: 0, note: "This is life-threatening — never delay." },
        { t: "Reassure the owner it's mild.", score: 0, note: "It is severe, and prognosis isn't the nurse's to give." },
      ],
    },
  },

  {
    id: "jasper-liver",
    name: "Jasper",
    species: "dog",
    breed: "Labrador Retriever",
    signalment: "7 yr · male neutered",
    presentation: "Vomiting and yellow gums; on long-term phenobarbital for epilepsy.",
    requested: "Biochemistry + CBC",
    handling: {
      prompt: "How do you collect for an accurate biochemistry?",
      options: [
        { t: "Serum/gel tube — allow it to clot fully, then centrifuge and separate promptly; avoid haemolysis. EDTA for the CBC.", score: 3, note: "Prompt separation and gentle handling give clean liver-enzyme and bilirubin results." },
        { t: "Lithium-heparin (green) plasma is acceptable for biochemistry if serum is unavailable.", score: 1, note: "Heparin plasma is usable for many analytes but serum is the standard reference sample." },
        { t: "EDTA tube for the biochemistry.", score: 0, note: "EDTA interferes with several biochemistry analytes (Ca, ALP, K)." },
        { t: "Shake the serum tube to make it clot faster.", score: 0, note: "Shaking haemolyses the sample." },
      ],
    },
    qc: {
      prompt: "Tap every pre-analytical issue you should flag.",
      checklist: [
        { t: "Serum is lipaemic (cloudy) — the patient was not fasted", flag: true, coach: "Lipaemia interferes with many assays; a fasted sample is preferred." },
        { t: "Serum has a pink tinge (mild haemolysis)", flag: true, coach: "Haemolysis falsely affects bilirubin, some enzymes and potassium." },
        { t: "Patient is on phenobarbital — expect induced ALP/ALT", flag: true, coach: "Note the medication; enzyme induction is relevant to interpretation." },
        { t: "Serum separated promptly and refrigerated", flag: false, coach: "" },
        { t: "Correctly labelled", flag: false, coach: "" },
        { t: "No clots in the EDTA", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "12.0", u: "×10⁹/L", ref: "6.0–17.0", flag: null },
        { a: "Neutrophils", v: "9.0", u: "×10⁹/L", ref: "3.0–11.5", flag: null },
        { a: "PCV", v: "44", u: "%", ref: "37–55", flag: null },
        { a: "Platelets", v: "300", u: "×10⁹/L", ref: "200–500", flag: null },
      ],
      options: [
        { t: "Haematology is largely unremarkable — the changes are biochemical.", score: 3, note: "Correct: the CBC is within range; focus on the biochemistry." },
        { t: "Mild stress leukogram.", score: 1, note: "Not really — counts are within range." },
        { t: "Marked inflammation.", score: 0, note: "WBC and neutrophils are normal." },
        { t: "Severe anaemia.", score: 0, note: "PCV is normal." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "ALT", v: "480", u: "U/L", ref: "10–100", flag: "high" },
        { a: "ALP", v: "1100", u: "U/L", ref: "23–212", flag: "high" },
        { a: "Total bilirubin", v: "60", u: "µmol/L", ref: "0–15", flag: "high" },
        { a: "Albumin", v: "24", u: "g/L", ref: "26–40", flag: "low" },
        { a: "Glucose", v: "4.5", u: "mmol/L", ref: "3.3–6.5", flag: null },
      ],
      options: [
        { t: "Marked hepatocellular (ALT) and cholestatic (ALP) enzyme elevation with hyperbilirubinaemia — hepatobiliary disease; phenobarbital and sample artefact may contribute.", score: 3, note: "The pattern points to liver/biliary disease; note drug induction and the lipaemia/haemolysis caveats." },
        { t: "Raised liver enzymes — recommend a repeat sample.", score: 1, note: "A clean repeat is wise, but the elevations and jaundice clearly indicate hepatobiliary disease." },
        { t: "Renal azotaemia.", score: 0, note: "Urea/creatinine aren't the issue here." },
        { t: "Normal biochemistry.", score: 0, note: "Enzymes and bilirubin are markedly elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet; recommend a fasted, haemolysis-free repeat to remove lipaemia/haemolysis artefact, and document the phenobarbital therapy.", score: 3, note: "Addresses the artefacts and the drug history that affect interpretation." },
        { t: "Flag the results to the vet.", score: 1, note: "Good, but the repeat sample and drug note add real value." },
        { t: "File as normal.", score: 0, note: "Results are clearly abnormal." },
        { t: "Stop the phenobarbital.", score: 0, note: "Never alter epilepsy medication — that's a veterinary decision." },
      ],
    },
  },

  {
    id: "daisy-pyometra",
    name: "Daisy",
    species: "dog",
    breed: "Golden Retriever",
    signalment: "6 yr · female entire",
    presentation: "Lethargic, PU/PD and a vulval discharge ~4 weeks after her last season.",
    requested: "CBC + biochemistry",
    handling: {
      prompt: "How do you collect to best assess the white cells?",
      options: [
        { t: "EDTA for the CBC (invert to mix) and serum for biochemistry; make a fresh smear to assess neutrophil morphology and a left shift.", score: 3, note: "A fresh smear lets you confirm bands/toxic change that the analyser may miss." },
        { t: "EDTA for the CBC only — no smear needed.", score: 1, note: "A manual smear is important when sepsis/left shift is suspected." },
        { t: "Heparin tube for the CBC.", score: 0, note: "EDTA, not heparin, is the haematology tube." },
        { t: "Run the CBC tomorrow from refrigerated blood.", score: 0, note: "Delay degrades cell morphology." },
      ],
    },
    qc: {
      prompt: "Tap every finding/issue you should flag.",
      checklist: [
        { t: "Smear shows band (immature) neutrophils and toxic change", flag: true, coach: "A left shift with toxic change indicates significant inflammation/sepsis — report promptly." },
        { t: "Analyser flags a marked leukocytosis", flag: true, coach: "A very high WBC should be verified and characterised on a manual differential." },
        { t: "Only an analyser count was run — no manual differential", flag: true, coach: "Confirm the differential manually when serious inflammation is suspected." },
        { t: "EDTA gently inverted, fresh smear made", flag: false, coach: "" },
        { t: "Tube correctly filled and labelled", flag: false, coach: "" },
        { t: "No haemolysis or lipaemia", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "38.0", u: "×10⁹/L", ref: "6.0–17.0", flag: "high" },
        { a: "Neutrophils", v: "32.0", u: "×10⁹/L", ref: "3.0–11.5", flag: "high" },
        { a: "Band neutrophils", v: "3.5", u: "×10⁹/L", ref: "0–0.3", flag: "high" },
        { a: "Lymphocytes", v: "1.2", u: "×10⁹/L", ref: "1.0–4.8", flag: null },
        { a: "PCV", v: "35", u: "%", ref: "37–55", flag: "low" },
      ],
      options: [
        { t: "Marked neutrophilia with a left shift (band neutrophils) — an inflammatory/infectious leukogram; with the history, consistent with pyometra/sepsis.", score: 3, note: "Bands + toxic change + the clinical picture strongly suggest pyometra." },
        { t: "Stress leukogram.", score: 0, note: "Stress doesn't cause a left shift with band neutrophils." },
        { t: "Leukaemia.", score: 0, note: "This is a reactive inflammatory pattern, not neoplasia." },
        { t: "Normal haematology.", score: 0, note: "There is a marked leukocytosis." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Globulin", v: "52", u: "g/L", ref: "20–40", flag: "high" },
        { a: "Urea", v: "11.0", u: "mmol/L", ref: "3.5–9.0", flag: "high" },
        { a: "Creatinine", v: "140", u: "µmol/L", ref: "40–130", flag: "high" },
        { a: "Albumin", v: "25", u: "g/L", ref: "26–40", flag: "low" },
      ],
      options: [
        { t: "Hyperglobulinaemia with mild azotaemia — consistent with systemic inflammation/sepsis and possible dehydration.", score: 3, note: "Inflammation raises globulins; mild azotaemia fits dehydration/early renal effects." },
        { t: "Mild azotaemia — just check hydration.", score: 1, note: "Hydration matters, but the high globulin and clinical picture point to systemic inflammation." },
        { t: "Liver failure.", score: 0, note: "Liver enzymes aren't reported as the problem." },
        { t: "Normal biochemistry.", score: 0, note: "Globulin and urea are elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Treat as urgent — alert the vet promptly (possible pyometra/sepsis) and confirm the left shift on a manual differential.", score: 3, note: "Pyometra is a surgical emergency; verify the smear findings." },
        { t: "Flag the results to the vet.", score: 1, note: "Right, but emphasise urgency and the manual confirmation." },
        { t: "Recheck in a week.", score: 0, note: "Far too slow for a septic patient." },
        { t: "Advise the owner on a diagnosis.", score: 0, note: "Diagnosis/communication is the vet's role." },
      ],
    },
  },

  {
    id: "coco-addisons",
    name: "Coco",
    species: "dog",
    breed: "Standard Poodle",
    signalment: "3 yr · female spayed",
    presentation: "Intermittent vomiting and lethargy; collapsed today and is bradycardic.",
    requested: "Biochemistry + electrolytes + CBC",
    handling: {
      prompt: "Potassium accuracy is critical here. How do you collect?",
      options: [
        { t: "Serum/gel for biochemistry, separated promptly with no haemolysis (critical for accurate potassium); EDTA for the CBC.", score: 3, note: "Prompt, haemolysis-free serum gives a trustworthy potassium." },
        { t: "Lithium-heparin plasma, separated immediately.", score: 1, note: "Acceptable if separated at once, though serum is the reference." },
        { t: "EDTA tube for the biochemistry.", score: 0, note: "Potassium-EDTA grossly falsely elevates potassium — never use it for K." },
        { t: "Leave the serum on the clot for several hours before spinning.", score: 0, note: "Delay leaks potassium from cells, falsely raising it." },
      ],
    },
    qc: {
      prompt: "Tap every issue you should flag before trusting the potassium.",
      checklist: [
        { t: "The serum is haemolysed (red tinge)", flag: true, coach: "Haemolysis falsely raises potassium — distinguish artefact from true hyperkalaemia." },
        { t: "Sample was separated 3 h after collection", flag: true, coach: "Delayed separation leaks potassium from cells." },
        { t: "Bradycardia clinically fits a true high potassium", flag: true, coach: "Clinical correlation suggests the hyperkalaemia may be real and dangerous — don't simply dismiss it." },
        { t: "Tube correctly labelled", flag: false, coach: "" },
        { t: "EDTA filled to the line", flag: false, coach: "" },
        { t: "No lipaemia", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "8.0", u: "×10⁹/L", ref: "6.0–17.0", flag: null },
        { a: "Lymphocytes", v: "4.6", u: "×10⁹/L", ref: "1.0–4.8", flag: null },
        { a: "Eosinophils", v: "1.4", u: "×10⁹/L", ref: "0.1–1.25", flag: "high" },
        { a: "PCV", v: "42", u: "%", ref: "37–55", flag: null },
      ],
      options: [
        { t: "No stress leukogram (lymphocytes high-normal, eosinophilia) in a very sick dog — a clue to hypoadrenocorticism (Addison's).", score: 3, note: "A sick dog that lacks the expected stress leukogram should raise Addison's." },
        { t: "Unremarkable haematology, nothing to note.", score: 1, note: "Counts look near-normal, but the absence of a stress response is itself the clue." },
        { t: "Severe inflammation.", score: 0, note: "No neutrophilia or left shift." },
        { t: "Marked anaemia.", score: 0, note: "PCV is normal." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry/electrolytes and choose the best interpretation.",
      panel: [
        { a: "Potassium", v: "7.8", u: "mmol/L", ref: "3.5–5.8", flag: "high" },
        { a: "Sodium", v: "132", u: "mmol/L", ref: "144–160", flag: "low" },
        { a: "Na:K ratio", v: "17", u: "", ref: ">27", flag: "low" },
        { a: "Urea", v: "22", u: "mmol/L", ref: "3.5–9.0", flag: "high" },
        { a: "Creatinine", v: "220", u: "µmol/L", ref: "40–130", flag: "high" },
        { a: "Glucose", v: "3.0", u: "mmol/L", ref: "3.3–6.5", flag: "low" },
      ],
      options: [
        { t: "Severe hyperkalaemia with hyponatraemia (low Na:K ratio) and azotaemia — classic for hypoadrenocorticism (Addison's); the hyperkalaemia is life-threatening.", score: 3, note: "The Na:K ratio <27 with this picture is the hallmark of Addison's." },
        { t: "Azotaemia — likely primary renal disease.", score: 1, note: "Azotaemia is present, but the low Na:K and clinical picture point to Addison's." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is low, not high." },
        { t: "Normal electrolytes.", score: 0, note: "Potassium and sodium are markedly abnormal." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Treat as an emergency — alert the vet immediately about the severe hyperkalaemia (cardiac risk), and draw a fresh, haemolysis-free sample to confirm it isn't artefactual.", score: 3, note: "Acts on a life-threatening result while controlling for the haemolysis artefact." },
        { t: "Flag the potassium to the vet.", score: 1, note: "Right, but stress the urgency and confirm with a clean sample." },
        { t: "Refrigerate and run it again tomorrow.", score: 0, note: "A potassium of 7.8 can't wait." },
        { t: "Assume it's all haemolysis artefact and ignore it.", score: 0, note: "Dangerous — the clinical signs suggest it may be real." },
      ],
    },
  },

  {
    id: "tigger-feline-ckd",
    name: "Tigger",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "15 yr · male neutered",
    presentation: "Weight loss, PU/PD, a poor coat and bad breath.",
    requested: "Biochemistry + electrolytes + CBC + USG",
    handling: {
      prompt: "How do you collect to interpret a possible renal azotaemia?",
      options: [
        { t: "Serum/gel for biochemistry (separate promptly), EDTA for the CBC, and collect urine for specific gravity to interpret the azotaemia.", score: 3, note: "USG is essential to classify azotaemia as renal vs pre-renal." },
        { t: "Biochemistry and CBC tubes only — USG can wait for another day.", score: 1, note: "Without USG you can't confirm the kidneys are failing to concentrate urine." },
        { t: "EDTA for everything.", score: 0, note: "EDTA isn't suitable for biochemistry." },
        { t: "Run biochemistry straight off the clot without separating.", score: 0, note: "Unseparated serum gives leakage artefact." },
      ],
    },
    qc: {
      prompt: "Tap every issue you should flag.",
      checklist: [
        { t: "USG was not collected — the azotaemia can't be classified", flag: true, coach: "Renal vs pre-renal azotaemia hinges on urine concentrating ability (USG)." },
        { t: "Mild haemolysis is present in the serum", flag: true, coach: "Haemolysis affects potassium and phosphate results." },
        { t: "Potassium is borderline — could be affected by haemolysis", flag: true, coach: "Interpret electrolytes cautiously when the sample is haemolysed." },
        { t: "Serum separated promptly and clear", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
        { t: "No clots in the EDTA", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "PCV", v: "22", u: "%", ref: "30–45", flag: "low" },
        { a: "RBC", v: "5.0", u: "×10¹²/L", ref: "6.0–10.0", flag: "low" },
        { a: "Reticulocytes", v: "8", u: "×10⁹/L", ref: ">50 if regenerating", flag: "low" },
        { a: "WBC", v: "9.0", u: "×10⁹/L", ref: "5.5–19.5", flag: null },
      ],
      options: [
        { t: "Non-regenerative anaemia — consistent with chronic kidney disease (reduced erythropoietin).", score: 3, note: "Low PCV with low reticulocytes fits CKD in an older cat." },
        { t: "Mild anaemia — recommend a reticulocyte count.", score: 1, note: "Reticulocytes are already shown to be low/inadequate." },
        { t: "Regenerative/blood-loss anaemia.", score: 0, note: "Reticulocytes are low, so not regenerative." },
        { t: "Normal haematology.", score: 0, note: "PCV is below range." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Urea", v: "30", u: "mmol/L", ref: "5.0–11.0", flag: "high" },
        { a: "Creatinine", v: "380", u: "µmol/L", ref: "70–180", flag: "high" },
        { a: "Phosphate", v: "2.6", u: "mmol/L", ref: "0.9–1.9", flag: "high" },
        { a: "Potassium", v: "3.2", u: "mmol/L", ref: "3.5–5.5", flag: "low" },
        { a: "Glucose", v: "6.0", u: "mmol/L", ref: "3.3–6.5", flag: null },
      ],
      options: [
        { t: "Azotaemia with hyperphosphataemia and hypokalaemia — typical feline CKD; interpret with a USG (expect poorly concentrated urine).", score: 3, note: "This electrolyte/azotaemia pattern is classic for feline CKD." },
        { t: "Azotaemia — possibly pre-renal from dehydration.", score: 1, note: "Possible, but the hyperphosphataemia, hypokalaemia and non-regenerative anaemia favour renal; USG confirms." },
        { t: "Liver disease.", score: 0, note: "Liver analytes aren't the problem." },
        { t: "Normal biochemistry.", score: 0, note: "Urea, creatinine and phosphate are elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet; obtain a USG to confirm the renal origin and recommend a haemolysis-free repeat for potassium.", score: 3, note: "Completes the renal work-up and removes the haemolysis artefact." },
        { t: "Flag the results to the vet.", score: 1, note: "Good, but the USG and clean repeat make the report definitive." },
        { t: "File as normal.", score: 0, note: "Results are abnormal." },
        { t: "Tell the owner the cat has kidney failure.", score: 0, note: "Communicating the diagnosis is the vet's role." },
      ],
    },
  },

  {
    id: "bruno-eosinophilia",
    name: "Bruno",
    species: "dog",
    breed: "Boxer",
    signalment: "2 yr · male neutered",
    presentation: "Chronic itching and occasional diarrhoea; worming history is incomplete.",
    requested: "CBC + biochemistry + faecal",
    handling: {
      prompt: "How do you collect the three samples?",
      options: [
        { t: "EDTA for the CBC (fresh smear for the differential), serum for biochemistry, and a separate fresh faecal sample for parasitology — kept apart from the blood.", score: 3, note: "Right tubes, fresh smear, and an uncontaminated faecal sample." },
        { t: "EDTA for the CBC and biochemistry.", score: 1, note: "EDTA isn't ideal for biochemistry; use serum." },
        { t: "Put the faecal sample in the EDTA tube to save containers.", score: 0, note: "Never mix faeces with blood tubes — both samples become useless." },
        { t: "Refrigerate the blood and make the smear next week.", score: 0, note: "Smears must be made fresh." },
      ],
    },
    qc: {
      prompt: "Tap every issue you should flag.",
      checklist: [
        { t: "A manual differential is needed to confirm the eosinophilia", flag: true, coach: "Analyser eosinophil counts should be verified on a smear." },
        { t: "Worming history is incomplete — directly relevant to eosinophilia", flag: true, coach: "Parasitism is a leading cause of eosinophilia; the history matters." },
        { t: "Analyser flags a high eosinophil count", flag: true, coach: "A flagged result should be confirmed manually." },
        { t: "Faecal sample is fresh and in a separate container", flag: false, coach: "" },
        { t: "Smear made fresh from well-mixed EDTA", flag: false, coach: "" },
        { t: "Samples correctly labelled", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "18.0", u: "×10⁹/L", ref: "6.0–17.0", flag: "high" },
        { a: "Eosinophils", v: "3.2", u: "×10⁹/L", ref: "0.1–1.25", flag: "high" },
        { a: "Neutrophils", v: "9.0", u: "×10⁹/L", ref: "3.0–11.5", flag: null },
        { a: "PCV", v: "46", u: "%", ref: "37–55", flag: null },
      ],
      options: [
        { t: "Marked eosinophilia — commonly parasitism or hypersensitivity/allergic disease; confirm on a manual differential and check the faecal.", score: 3, note: "Eosinophilia + itching + diarrhoea + poor worming points to parasites/allergy." },
        { t: "Mild inflammatory leukogram.", score: 1, note: "The standout feature is the eosinophilia specifically." },
        { t: "Stress leukogram.", score: 0, note: "Stress causes eosinopenia, not eosinophilia." },
        { t: "Normal haematology.", score: 0, note: "Eosinophils are clearly elevated." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "ALT", v: "70", u: "U/L", ref: "10–100", flag: null },
        { a: "Albumin", v: "32", u: "g/L", ref: "26–40", flag: null },
        { a: "Urea", v: "6.0", u: "mmol/L", ref: "3.5–9.0", flag: null },
        { a: "Total protein", v: "68", u: "g/L", ref: "54–75", flag: null },
      ],
      options: [
        { t: "Biochemistry is unremarkable — the key finding is haematological (the eosinophilia).", score: 3, note: "Correct: focus the report on the eosinophilia and parasitology." },
        { t: "Mild non-specific changes only.", score: 1, note: "Everything is in range, so there are no real changes." },
        { t: "Liver disease.", score: 0, note: "Liver analytes are normal." },
        { t: "Renal disease.", score: 0, note: "Urea is normal." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report the eosinophilia to the vet, submit the fresh faecal for parasitology and confirm the count on a manual differential.", score: 3, note: "Links the finding to the most useful next tests." },
        { t: "Flag the results to the vet.", score: 1, note: "Good, but naming the faecal/differential follow-up adds value." },
        { t: "Ignore it — eosinophils don't matter.", score: 0, note: "Eosinophilia is clinically important." },
        { t: "Start a wormer without veterinary direction.", score: 0, note: "Report and investigate first; treatment is directed by the vet." },
      ],
    },
  },

  {
    id: "bailey-cushings",
    name: "Bailey",
    species: "dog",
    breed: "Boxer",
    signalment: "9 yr · male neutered",
    presentation: "Pot-bellied, ravenous, thinning coat and marked PU/PD.",
    requested: "CBC + biochemistry",
    handling: {
      prompt: "Which tubes/handling do you select?",
      options: [
        { t: "EDTA (lavender) for the CBC — invert gently — and a serum/gel tube for biochemistry; spin and separate promptly.", score: 3, note: "Correct: EDTA for haematology, serum for biochemistry, separated quickly to avoid artefact." },
        { t: "EDTA for both tests to save a tube.", score: 1, note: "EDTA contaminates several biochemistry analytes (Ca, ALP, K) — use serum." },
        { t: "Serum for both; shake to clot faster.", score: 0, note: "Serum can't run a CBC and shaking causes haemolysis." },
        { t: "Fluoride-oxalate (grey) for everything.", score: 0, note: "Grey tops are for glucose preservation, not a general panel." },
      ],
    },
    qc: {
      prompt: "Tap every pre-analytical concern you should flag.",
      checklist: [
        { t: "ALP is markedly raised but is a non-specific finding", flag: true, coach: "A high ALP supports Cushing's but isn't diagnostic — note that dynamic testing is needed." },
        { t: "No dynamic screening test (ACTH stim / LDDST) has been booked", flag: true, coach: "Cushing's must be confirmed with a dynamic test, not enzyme changes alone." },
        { t: "A urine sample for culture wasn't collected (silent UTIs are common)", flag: true, coach: "Cushingoid dogs often have occult UTIs — a cystocentesis culture is worthwhile." },
        { t: "Serum separated promptly and clear", flag: false, coach: "" },
        { t: "Tube labelled and filled correctly", flag: false, coach: "" },
        { t: "EDTA gently inverted to mix", flag: false, coach: "" },
        { t: "No lipaemia noted", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "Neutrophils", v: "14.0", u: "×10⁹/L", ref: "3.0–11.5", flag: "high" },
        { a: "Lymphocytes", v: "0.6", u: "×10⁹/L", ref: "1.0–4.8", flag: "low" },
        { a: "Eosinophils", v: "0.05", u: "×10⁹/L", ref: "0.1–1.25", flag: "low" },
        { a: "PCV", v: "48", u: "%", ref: "37–55", flag: null },
      ],
      options: [
        { t: "Stress (steroid) leukogram — neutrophilia with lymphopenia and eosinopenia, expected with excess cortisol.", score: 3, note: "The classic steroid leukogram supports hyperadrenocorticism in context." },
        { t: "Inflammatory leukogram with a left shift.", score: 1, note: "No band neutrophils — this is a stress pattern, not active inflammation." },
        { t: "Leukaemia.", score: 0, note: "These mild changes don't indicate neoplasia." },
        { t: "Normal haematology.", score: 0, note: "There's a neutrophilia with lymphopenia and eosinopenia." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "ALP", v: "1450", u: "U/L", ref: "23–212", flag: "high" },
        { a: "ALT", v: "160", u: "U/L", ref: "10–100", flag: "high" },
        { a: "Cholesterol", v: "9.8", u: "mmol/L", ref: "3.0–7.0", flag: "high" },
        { a: "Glucose", v: "6.9", u: "mmol/L", ref: "3.3–6.5", flag: "high" },
        { a: "Urea", v: "5.0", u: "mmol/L", ref: "3.5–9.0", flag: null },
      ],
      options: [
        { t: "Markedly raised ALP with hypercholesterolaemia and mild hyperglycaemia — supportive of hyperadrenocorticism; confirm with a dynamic test.", score: 3, note: "Supportive but non-specific — ACTH stim or LDDST confirms the diagnosis." },
        { t: "Diabetes mellitus is confirmed.", score: 0, note: "Glucose is only mildly raised — not diagnostic." },
        { t: "Primary liver failure.", score: 1, note: "Enzymes are induced, but there's no synthetic-failure pattern; the picture fits cortisol excess." },
        { t: "Normal biochemistry.", score: 0, note: "ALP, ALT and cholesterol are elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet and recommend a dynamic screening test (ACTH stimulation or LDDST) plus a urine culture.", score: 3, note: "Confirms Cushing's and checks for an occult UTI before treatment." },
        { t: "Flag the high ALP to the vet.", score: 1, note: "Right direction, but naming the confirmatory test adds value." },
        { t: "Start trilostane based on the ALP.", score: 0, note: "Never start treatment without confirmatory testing." },
        { t: "File as normal — ALP is always high in older dogs.", score: 0, note: "An ALP of 1450 with these signs is significant." },
      ],
    },
  },

  {
    id: "milo-hyperthyroid",
    name: "Milo",
    species: "cat",
    breed: "Domestic Shorthair",
    signalment: "13 yr · male neutered",
    presentation: "Weight loss despite a big appetite, hyperactivity and a fast heart rate.",
    requested: "Biochemistry + total T4 + CBC",
    handling: {
      prompt: "How do you collect for a reliable thyroid and biochemistry result?",
      options: [
        { t: "Serum/gel for the T4 and biochemistry (separate promptly), EDTA for the CBC; handle the stressed cat calmly to limit struggling.", score: 3, note: "Serum is standard; low-stress handling reduces stress artefacts in a hyperthyroid cat." },
        { t: "EDTA plasma for the T4.", score: 1, note: "Serum is the standard sample for total T4; check the lab's requirements." },
        { t: "Leave whole blood at room temperature overnight, run in the morning.", score: 0, note: "Delay and warmth degrade the sample quality." },
        { t: "Fluoride-oxalate tube for the whole panel.", score: 0, note: "Grey tops are for glucose, not a thyroid/biochemistry panel." },
      ],
    },
    qc: {
      prompt: "Tap every pre-analytical concern you should flag.",
      checklist: [
        { t: "The cat was highly stressed and struggling during collection", flag: true, coach: "Stress can transiently affect some analytes and the patient's safety — use low-stress technique." },
        { t: "Creatinine looks normal — hyperthyroidism can MASK concurrent CKD", flag: true, coach: "A high metabolic rate increases renal blood flow; recheck kidney values once euthyroid." },
        { t: "Mild ALT/ALP rises may be secondary to hyperthyroidism", flag: true, coach: "These usually resolve with treatment — note them as likely secondary." },
        { t: "Serum separated promptly and clear", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
        { t: "EDTA gently inverted, no clots", flag: false, coach: "" },
        { t: "No lipaemia", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "PCV", v: "46", u: "%", ref: "30–45", flag: "high" },
        { a: "WBC", v: "10.0", u: "×10⁹/L", ref: "5.5–19.5", flag: null },
        { a: "Neutrophils", v: "7.0", u: "×10⁹/L", ref: "2.5–12.5", flag: null },
        { a: "Lymphocytes", v: "2.0", u: "×10⁹/L", ref: "1.5–7.0", flag: null },
      ],
      options: [
        { t: "Mildly raised PCV with an otherwise unremarkable leukogram — the diagnosis is biochemical (the T4).", score: 3, note: "A high metabolic/erythropoietic drive can mildly raise the PCV; focus on the T4." },
        { t: "Marked inflammation.", score: 0, note: "The white cells are within range." },
        { t: "Severe anaemia.", score: 0, note: "The PCV is high-normal/raised, not low." },
        { t: "Stress leukogram with a left shift.", score: 0, note: "No neutrophilia/lymphopenia or left shift is shown." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry/thyroid panel and choose the best interpretation.",
      panel: [
        { a: "Total T4", v: "105", u: "nmol/L", ref: "15–50", flag: "high" },
        { a: "ALT", v: "170", u: "U/L", ref: "10–100", flag: "high" },
        { a: "ALP", v: "125", u: "U/L", ref: "14–111", flag: "high" },
        { a: "Creatinine", v: "115", u: "µmol/L", ref: "70–180", flag: null },
        { a: "Urea", v: "9.0", u: "mmol/L", ref: "5.0–11.0", flag: null },
      ],
      options: [
        { t: "Markedly elevated total T4 with mild secondary liver enzyme rises — consistent with hyperthyroidism; recheck kidney values once euthyroid.", score: 3, note: "High T4 + the clinical signs confirm hyperthyroidism; the enzymes typically settle with treatment." },
        { t: "Primary liver disease.", score: 0, note: "The high T4 explains the mild enzyme rises." },
        { t: "Chronic kidney disease.", score: 1, note: "Renal values look normal NOW, but hyperthyroidism can mask CKD — recheck after treatment." },
        { t: "Normal results.", score: 0, note: "A T4 of 105 is well above range." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet to confirm and discuss treatment, and recommend rechecking renal values once the cat is euthyroid.", score: 3, note: "Treat the thyroid, then reassess the kidneys — a key clinical caveat in older cats." },
        { t: "Flag the high T4 to the vet.", score: 1, note: "Good, but the renal-masking caveat adds value." },
        { t: "Reassure the owner it's just old age.", score: 0, note: "These are treatable disease findings." },
        { t: "Advise a calorie-restricted diet for the weight loss.", score: 0, note: "Treat the cause; don't restrict calories." },
      ],
    },
  },

  {
    id: "rocky-sepsis",
    name: "Rocky",
    species: "dog",
    breed: "Bernese Mountain Dog",
    signalment: "5 yr · male neutered",
    presentation: "Febrile, collapsed and tachypnoeic after a penetrating bite wound to the chest.",
    requested: "CBC + blood smear + biochemistry + lactate",
    handling: {
      prompt: "How do you collect to best assess for sepsis?",
      options: [
        { t: "EDTA for the CBC (make a FRESH smear for a manual differential), serum for biochemistry, and a prompt lactate on the appropriate tube/analyser.", score: 3, note: "A fresh smear reveals a left shift/toxic change; lactate must be measured promptly." },
        { t: "EDTA for the CBC only — the analyser differential is enough.", score: 1, note: "When sepsis is suspected, confirm the differential and toxic change on a manual smear." },
        { t: "Heparin tube for the CBC.", score: 0, note: "EDTA, not heparin, is the haematology tube." },
        { t: "Delay everything until the morning.", score: 0, note: "A collapsing septic patient needs immediate point-of-care testing." },
      ],
    },
    qc: {
      prompt: "Tap every finding/issue you should flag.",
      checklist: [
        { t: "Smear shows band neutrophils with toxic change", flag: true, coach: "A left shift with toxic change indicates severe inflammation/sepsis — report promptly." },
        { t: "Lactate sample sat unspun for 30+ minutes", flag: true, coach: "Lactate rises in vitro if blood is left unprocessed — run it promptly for an accurate value." },
        { t: "Only an analyser count was run — no manual differential", flag: true, coach: "Confirm the differential manually when sepsis is suspected." },
        { t: "Fresh smear made from well-mixed EDTA", flag: false, coach: "" },
        { t: "Samples labelled correctly", flag: false, coach: "" },
        { t: "No clots in the EDTA", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "2.5", u: "×10⁹/L", ref: "6.0–17.0", flag: "low" },
        { a: "Neutrophils", v: "1.2", u: "×10⁹/L", ref: "3.0–11.5", flag: "low" },
        { a: "Band neutrophils", v: "0.8", u: "×10⁹/L", ref: "0–0.3", flag: "high" },
        { a: "Toxic change", v: "Present", u: "", ref: "absent", flag: "high" },
        { a: "PCV", v: "40", u: "%", ref: "37–55", flag: null },
      ],
      options: [
        { t: "A degenerative left shift — neutropenia WITH band neutrophils and toxic change — indicating overwhelming consumption in severe sepsis.", score: 3, note: "Neutropenia plus a left shift and toxic change is a danger sign of overwhelming infection (consumption outpacing supply)." },
        { t: "A reassuring low white count — infection is resolving.", score: 0, note: "A neutropenia with bands and toxic change is the opposite of reassuring — it signals consumption in sepsis." },
        { t: "Stress leukogram.", score: 0, note: "Stress raises neutrophils; here they're consumed and a left shift is present." },
        { t: "Normal haematology.", score: 0, note: "Neutropenia with toxic bands is markedly abnormal." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Lactate", v: "6.0", u: "mmol/L", ref: "0.5–2.5", flag: "high" },
        { a: "Glucose", v: "3.0", u: "mmol/L", ref: "3.3–6.5", flag: "low" },
        { a: "Albumin", v: "20", u: "g/L", ref: "26–40", flag: "low" },
        { a: "Urea", v: "12.0", u: "mmol/L", ref: "3.5–9.0", flag: "high" },
      ],
      options: [
        { t: "Hyperlactataemia with hypoglycaemia and hypoalbuminaemia — consistent with sepsis/poor perfusion; the high lactate reflects shock.", score: 3, note: "Raised lactate + low glucose + low albumin fit septic shock — an emergency." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is LOW, not high." },
        { t: "Normal biochemistry.", score: 0, note: "Lactate, glucose and albumin are all abnormal." },
        { t: "Primary liver failure.", score: 1, note: "Low glucose/albumin can occur in liver failure, but with the wound, fever and lactate this is sepsis." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Treat as a critical emergency — alert the vet immediately, support with IV fluids/oxygen as directed, and prepare for source control and intensive monitoring.", score: 3, note: "Septic shock with a degenerative left shift needs immediate aggressive support." },
        { t: "Flag the results to the vet.", score: 1, note: "Right, but the severity warrants emphasising it's a critical emergency." },
        { t: "Recheck in the morning.", score: 0, note: "Septic shock cannot wait." },
        { t: "Reassure the owner it's a mild infection.", score: 0, note: "This is life-threatening sepsis." },
      ],
    },
  },

  {
    id: "luna-hypothyroid",
    name: "Luna",
    species: "dog",
    breed: "Dobermann",
    signalment: "6 yr · female spayed",
    presentation: "Weight gain, lethargy, heat-seeking and a dull, thinning coat.",
    requested: "Biochemistry + CBC + thyroid panel",
    handling: {
      prompt: "How do you collect for a reliable thyroid and lipid result?",
      options: [
        { t: "Serum/gel for biochemistry and the thyroid panel (separate promptly); EDTA for the CBC; ideally a fasted sample to avoid lipaemia.", score: 3, note: "Fasting limits lipaemia, which interferes with assays; serum is standard for thyroid testing." },
        { t: "Non-fasted serum is fine; lipaemia doesn't matter.", score: 1, note: "Lipaemia interferes with many analytes — a fasted sample is preferred." },
        { t: "EDTA for the thyroid panel.", score: 0, note: "Serum is the standard sample for thyroid hormones." },
        { t: "Run it straight off the clot without separating.", score: 0, note: "Unseparated serum causes leakage artefact." },
      ],
    },
    qc: {
      prompt: "Tap every pre-analytical concern you should flag.",
      checklist: [
        { t: "Serum is lipaemic — the patient wasn't fasted", flag: true, coach: "Lipaemia interferes with several assays — a fasted repeat is preferred." },
        { t: "Any concurrent illness/medication can lower T4 (sick-euthyroid)", flag: true, coach: "Non-thyroidal illness suppresses T4 — interpret with TSH and the clinical picture." },
        { t: "TSH wasn't requested alongside the T4", flag: true, coach: "Total T4 plus TSH improves diagnostic accuracy for hypothyroidism." },
        { t: "Serum separated promptly", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
        { t: "EDTA filled to the line", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "PCV", v: "34", u: "%", ref: "37–55", flag: "low" },
        { a: "RBC", v: "5.0", u: "×10¹²/L", ref: "5.5–8.5", flag: "low" },
        { a: "Reticulocytes", v: "25", u: "×10⁹/L", ref: ">60 if regenerating", flag: "low" },
        { a: "WBC", v: "9.0", u: "×10⁹/L", ref: "6.0–17.0", flag: null },
      ],
      options: [
        { t: "A mild non-regenerative anaemia — a recognised, non-specific finding in hypothyroidism.", score: 3, note: "Reduced metabolic drive on the marrow gives a mild non-regenerative anaemia in hypothyroidism." },
        { t: "A regenerative, blood-loss anaemia.", score: 0, note: "Reticulocytes are low — not regenerative." },
        { t: "Severe anaemia needing transfusion.", score: 0, note: "This is mild, not transfusion-level." },
        { t: "Normal haematology.", score: 1, note: "It's only mildly below range, but the slight anaemia fits the picture." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry/thyroid panel and choose the best interpretation.",
      panel: [
        { a: "Total T4", v: "5", u: "nmol/L", ref: "15–50", flag: "low" },
        { a: "TSH", v: "0.9", u: "ng/mL", ref: "<0.6", flag: "high" },
        { a: "Cholesterol", v: "13.0", u: "mmol/L", ref: "3.0–7.0", flag: "high" },
        { a: "Triglycerides", v: "4.0", u: "mmol/L", ref: "0.2–1.5", flag: "high" },
        { a: "ALT", v: "80", u: "U/L", ref: "10–100", flag: null },
      ],
      options: [
        { t: "Low T4 with a high TSH and marked hypercholesterolaemia — consistent with primary hypothyroidism.", score: 3, note: "Low T4 + high TSH + the lipid changes and clinical signs support hypothyroidism." },
        { t: "Hyperthyroidism.", score: 0, note: "T4 is LOW, not high — and hyperthyroidism is a feline disease." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose isn't the issue; the lipids and thyroid are." },
        { t: "Normal results.", score: 0, note: "T4, TSH, cholesterol and triglycerides are all abnormal." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet — the low T4 with high TSH and supportive lipids fits hypothyroidism; thyroid hormone supplementation is a veterinary decision.", score: 3, note: "The combined results confirm the diagnosis; treatment is started and monitored by the vet." },
        { t: "Flag the low T4 to the vet.", score: 1, note: "Good, but interpreting it WITH the TSH adds value." },
        { t: "Start levothyroxine yourself.", score: 0, note: "Starting medication is a veterinary decision." },
        { t: "File as a sick-euthyroid artefact and ignore it.", score: 0, note: "The high TSH and clinical signs point to true hypothyroidism." },
      ],
    },
  },

  {
    id: "shadow-hypercalcaemia",
    name: "Shadow",
    species: "dog",
    breed: "Labrador Retriever",
    signalment: "8 yr · male neutered",
    presentation: "PU/PD, inappetence and lethargy; enlarged peripheral lymph nodes on exam.",
    requested: "Biochemistry + CBC + ionised calcium",
    handling: {
      prompt: "Calcium accuracy matters. How do you collect?",
      options: [
        { t: "Serum/gel for biochemistry (separate promptly, avoid haemolysis); EDTA for the CBC; collect ionised calcium per the lab's anaerobic protocol.", score: 3, note: "EDTA chelates calcium, so never use it for calcium; ionised calcium needs careful anaerobic handling." },
        { t: "EDTA tube for the calcium measurement.", score: 0, note: "EDTA binds calcium and grossly lowers the result — never use it for calcium." },
        { t: "Lithium-heparin plasma, separated immediately.", score: 1, note: "Heparin plasma is usable for total calcium, but serum is the standard and ionised calcium needs special handling." },
        { t: "Leave the serum on the clot for hours before spinning.", score: 0, note: "Delay causes leakage artefact." },
      ],
    },
    qc: {
      prompt: "Tap every issue you should flag.",
      checklist: [
        { t: "Marked hypercalcaemia is a red flag — confirm it's not an EDTA/sample error", flag: true, coach: "Always confirm a high calcium on an appropriate sample (and check ionised calcium)." },
        { t: "Enlarged lymph nodes weren't aspirated", flag: true, coach: "Hypercalcaemia of malignancy (e.g. lymphoma) is a leading cause — node aspirates are key." },
        { t: "Ionised calcium not yet measured", flag: true, coach: "Ionised calcium is the biologically active fraction and best confirms true hypercalcaemia." },
        { t: "Serum separated promptly and clear", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
        { t: "EDTA filled and inverted", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "Lymphocytes", v: "10.0", u: "×10⁹/L", ref: "1.0–4.8", flag: "high" },
        { a: "WBC", v: "16.0", u: "×10⁹/L", ref: "6.0–17.0", flag: null },
        { a: "PCV", v: "37", u: "%", ref: "37–55", flag: null },
        { a: "Neutrophils", v: "6.0", u: "×10⁹/L", ref: "3.0–11.5", flag: null },
      ],
      options: [
        { t: "A lymphocytosis — with lymphadenopathy and hypercalcaemia, raising strong suspicion of lymphoma.", score: 3, note: "Lymphocytosis + big nodes + hypercalcaemia is a classic lymphoma trio — aspirate the nodes." },
        { t: "A stress leukogram.", score: 0, note: "Stress causes LYMPHOPENIA, not lymphocytosis." },
        { t: "Severe anaemia.", score: 0, note: "The PCV is at the low end of normal, not severely low." },
        { t: "Normal haematology.", score: 0, note: "The lymphocyte count is clearly raised." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Calcium (total)", v: "4.0", u: "mmol/L", ref: "2.3–3.0", flag: "high" },
        { a: "Ionised calcium", v: "1.9", u: "mmol/L", ref: "1.1–1.4", flag: "high" },
        { a: "Phosphate", v: "1.0", u: "mmol/L", ref: "0.8–1.6", flag: null },
        { a: "Urea", v: "13.0", u: "mmol/L", ref: "3.5–9.0", flag: "high" },
        { a: "Creatinine", v: "190", u: "µmol/L", ref: "40–130", flag: "high" },
      ],
      options: [
        { t: "Genuine hypercalcaemia (total AND ionised raised) with secondary azotaemia — investigate malignancy (e.g. lymphoma) urgently.", score: 3, note: "Both calcium fractions raised confirms true hypercalcaemia; protect the kidneys and find the cause." },
        { t: "Spurious hypercalcaemia from a sample error.", score: 0, note: "Ionised calcium is also high — this is real, not artefactual." },
        { t: "Primary kidney disease causing the high calcium.", score: 1, note: "CKD usually keeps calcium normal/low; the marked hypercalcaemia with nodes points to malignancy." },
        { t: "Normal biochemistry.", score: 0, note: "Calcium and renal values are abnormal." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Flag urgently to the vet — recommend node aspirates and imaging to find the cause, and support the kidneys with IV fluids as directed.", score: 3, note: "Find the cause of hypercalcaemia and protect the kidneys with fluids." },
        { t: "Flag the results to the vet.", score: 1, note: "Right, but naming the node aspirate/fluid support adds value." },
        { t: "Give a calcium supplement.", score: 0, note: "He has too much calcium already — that would be dangerous." },
        { t: "Recheck in a month.", score: 0, note: "Marked hypercalcaemia with azotaemia needs prompt action." },
      ],
    },
  },

  {
    id: "ruby-blood-loss",
    name: "Ruby",
    species: "dog",
    breed: "Weimaraner",
    signalment: "7 yr · female spayed",
    presentation: "Acutely weak and pale with a distended, painful abdomen; suspected bleeding splenic mass.",
    requested: "PCV/TP + CBC + biochemistry + coagulation",
    handling: {
      prompt: "How do you collect in a suspected acute bleed?",
      options: [
        { t: "EDTA for the CBC, a spun PCV/TP for a quick bedside answer, serum for biochemistry, and a citrate (blue) tube — filled to the line — for coagulation.", score: 3, note: "Citrate must be filled exactly to the line for valid coagulation times; a spun PCV/TP gives an instant read." },
        { t: "Underfill the citrate tube — close enough.", score: 0, note: "An underfilled citrate tube falsely prolongs clotting times — fill exactly to the line." },
        { t: "EDTA for the coagulation sample.", score: 0, note: "Coagulation testing requires citrate, not EDTA." },
        { t: "Skip the PCV/TP and wait for the lab CBC.", score: 1, note: "A bedside PCV/TP gives an immediate answer in an emergency — don't wait." },
      ],
    },
    qc: {
      prompt: "Tap every issue you should flag.",
      checklist: [
        { t: "Total protein is low alongside the low PCV", flag: true, coach: "A low PCV WITH low protein supports whole-blood loss rather than haemolysis." },
        { t: "Acute bleeding may look 'non-regenerative' for the first few days", flag: true, coach: "Reticulocytosis takes 3–5 days — early acute loss can lack regeneration; interpret with the timeline." },
        { t: "Citrate tube must be filled to the line for valid coagulation", flag: true, coach: "Under/over-filling invalidates clotting times — check the fill." },
        { t: "Spun PCV/TP read promptly", flag: false, coach: "" },
        { t: "Samples labelled correctly", flag: false, coach: "" },
        { t: "No clots in the EDTA", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "PCV", v: "22", u: "%", ref: "37–55", flag: "low" },
        { a: "Total protein", v: "44", u: "g/L", ref: "54–75", flag: "low" },
        { a: "Reticulocytes", v: "35", u: "×10⁹/L", ref: ">60 if regenerating", flag: "low" },
        { a: "Platelets", v: "190", u: "×10⁹/L", ref: "200–500", flag: "low" },
      ],
      options: [
        { t: "Acute haemorrhage — anaemia WITH a low total protein; regeneration hasn't appeared yet.", score: 3, note: "Low PCV + low protein after acute collapse points to whole-blood loss." },
        { t: "Immune-mediated haemolytic anaemia.", score: 0, note: "Haemolysis usually keeps protein normal; the low protein here fits loss." },
        { t: "Non-regenerative anaemia of chronic disease.", score: 0, note: "This is an acute bleed; chronic disease wouldn't present this way." },
        { t: "Normal haematology.", score: 0, note: "A PCV of 22% with low protein is markedly abnormal." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry/coagulation and choose the best interpretation.",
      panel: [
        { a: "Albumin", v: "20", u: "g/L", ref: "26–40", flag: "low" },
        { a: "PT", v: "9", u: "s", ref: "7–11", flag: null },
        { a: "APTT", v: "14", u: "s", ref: "12–18", flag: null },
        { a: "Glucose", v: "5.0", u: "mmol/L", ref: "3.3–6.5", flag: null },
      ],
      options: [
        { t: "Low albumin from whole-blood loss with normal clotting times — supports surgical haemorrhage (e.g. a bleeding mass), not a coagulopathy.", score: 3, note: "Normal PT/APTT make a clotting-factor problem unlikely; the bleed is structural/surgical." },
        { t: "A clotting-factor disorder (e.g. rodenticide).", score: 1, note: "Possible differential, but the clotting times are normal here — investigate a bleeding mass." },
        { t: "Liver failure.", score: 0, note: "Clotting times and glucose are normal — no synthetic-failure pattern." },
        { t: "Normal biochemistry.", score: 0, note: "Albumin is clearly low." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Treat as an emergency — alert the vet immediately, support with IV fluids and prepare for imaging (abdominal FAST) and possible transfusion/surgery.", score: 3, note: "A bleeding abdominal mass needs urgent stabilisation, imaging and likely surgery." },
        { t: "Flag the results to the vet.", score: 1, note: "Right, but emphasise the emergency and the transfusion/surgery preparation." },
        { t: "Give vitamin K and send home.", score: 0, note: "Clotting times are normal — this isn't a rodenticide coagulopathy." },
        { t: "Recheck the PCV tomorrow.", score: 0, note: "An actively bleeding collapsing dog can't wait." },
      ],
    },
  },

  {
    id: "ollie-pancreatitis",
    name: "Ollie",
    species: "dog",
    breed: "Cocker Spaniel",
    signalment: "8 yr · male neutered",
    presentation: "Vomiting, a painful 'prayer-position' abdomen and inappetence after a fatty meal.",
    requested: "CBC + biochemistry + spec cPL",
    handling: {
      prompt: "How do you collect for an accurate biochemistry and lipase?",
      options: [
        { t: "Serum/gel for biochemistry and the specific cPL (separate promptly); EDTA for the CBC; a fasted sample reduces lipaemia.", score: 3, note: "Serum is standard; fasting limits lipaemia, which interferes with assays." },
        { t: "Non-fasted serum is fine even though he just ate a fatty meal.", score: 1, note: "Recent fat intake causes lipaemia that interferes with assays — note it and prefer a fasted sample." },
        { t: "EDTA for the lipase test.", score: 0, note: "EDTA isn't suitable for biochemistry/lipase assays." },
        { t: "Run it off the unspun clot.", score: 0, note: "Unseparated serum gives leakage artefact." },
      ],
    },
    qc: {
      prompt: "Tap every pre-analytical issue you should flag.",
      checklist: [
        { t: "Serum is markedly lipaemic (cloudy) after a fatty meal", flag: true, coach: "Lipaemia interferes with many analytes — note it; a fasted repeat may be needed." },
        { t: "The patient is dehydrated — interpret the PCV with that in mind", flag: true, coach: "Haemoconcentration from vomiting raises the PCV; correlate with hydration." },
        { t: "Spec cPL is the more specific marker than total lipase/amylase", flag: true, coach: "Specific canine pancreatic lipase is more reliable than older amylase/lipase for pancreatitis." },
        { t: "Serum separated promptly", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
        { t: "No clots in the EDTA", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "WBC", v: "24.0", u: "×10⁹/L", ref: "6.0–17.0", flag: "high" },
        { a: "Neutrophils", v: "20.0", u: "×10⁹/L", ref: "3.0–11.5", flag: "high" },
        { a: "PCV", v: "57", u: "%", ref: "37–55", flag: "high" },
        { a: "Platelets", v: "300", u: "×10⁹/L", ref: "200–500", flag: null },
      ],
      options: [
        { t: "An inflammatory leukogram (neutrophilia) with haemoconcentration (high PCV from dehydration) — fits acute pancreatitis.", score: 3, note: "Neutrophilia + a high PCV from dehydration matches an acute inflammatory abdomen." },
        { t: "Anaemia.", score: 0, note: "The PCV is high, not low." },
        { t: "Stress leukogram only.", score: 1, note: "There is a neutrophilia, but the inflammatory picture and dehydration are the key points." },
        { t: "Normal haematology.", score: 0, note: "WBC, neutrophils and PCV are all raised." },
      ],
    },
    biochem: {
      prompt: "Review the biochemistry and choose the best interpretation.",
      panel: [
        { a: "Spec cPL", v: "550", u: "µg/L", ref: "0–200", flag: "high" },
        { a: "ALT", v: "150", u: "U/L", ref: "10–100", flag: "high" },
        { a: "Glucose", v: "7.0", u: "mmol/L", ref: "3.3–6.5", flag: "high" },
        { a: "Urea", v: "10.0", u: "mmol/L", ref: "3.5–9.0", flag: "high" },
      ],
      options: [
        { t: "A markedly raised pancreatic lipase with reactive enzyme/stress changes — consistent with acute pancreatitis.", score: 3, note: "A high spec cPL in this clinical picture supports pancreatitis; the other changes are reactive/dehydration-related." },
        { t: "Diabetes mellitus.", score: 0, note: "Glucose is only mildly raised (stress/illness), not diabetic." },
        { t: "Chronic kidney disease.", score: 1, note: "Mild azotaemia here is likely pre-renal (dehydration); check USG/hydration before calling it renal." },
        { t: "Normal biochemistry.", score: 0, note: "The pancreatic lipase is markedly elevated." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Report to the vet and support the patient — IV fluids for dehydration, anti-emesis/analgesia and early nutritional support as directed.", score: 3, note: "Supportive care is the mainstay of pancreatitis management." },
        { t: "Flag the results to the vet.", score: 1, note: "Right, but naming the supportive care adds value." },
        { t: "Withhold fluids to 'rest' the pancreas.", score: 0, note: "Outdated/harmful — these dehydrated patients need IV fluids." },
        { t: "Discharge on a high-fat diet.", score: 0, note: "A low-fat diet is advised; fat can worsen pancreatitis." },
      ],
    },
  },

  {
    id: "willow-imt",
    name: "Willow",
    species: "dog",
    breed: "Cavalier King Charles Spaniel",
    signalment: "5 yr · female spayed",
    presentation: "Petechiae on the gums and belly, a recent nosebleed and bruising after a small knock.",
    requested: "CBC + blood smear",
    handling: {
      prompt: "How do you collect and prepare in a suspected bleeding disorder?",
      options: [
        { t: "EDTA for the CBC (invert gently) and make a FRESH smear to estimate platelets and check for clumping; handle gently and use a peripheral vein with care.", score: 3, note: "A fresh smear confirms the count and rules out clumping; gentle peripheral sampling avoids provoking bleeding." },
        { t: "Take a jugular sample for speed.", score: 0, note: "Avoid jugular/large-vessel puncture in a bleeding patient — use a peripheral vein with care." },
        { t: "EDTA only — trust the analyser platelet count.", score: 1, note: "Automated low platelet counts MUST be confirmed on a smear (clumping is common)." },
        { t: "Refrigerate and make the smear tomorrow.", score: 0, note: "Smears are best made fresh." },
      ],
    },
    qc: {
      prompt: "Tap every issue you should flag before acting on the low platelet count.",
      checklist: [
        { t: "Platelet clumping must be excluded on the smear (EDTA artefact)", flag: true, coach: "EDTA-induced clumping falsely lowers the analyser count — always check a smear." },
        { t: "A clot in the EDTA would invalidate the platelet count", flag: true, coach: "Any clot consumes platelets and invalidates the count — recollect if present." },
        { t: "Petechiae fit a true severe thrombocytopenia — handle with care", flag: true, coach: "If the count is genuinely very low, minimise venepuncture and rough handling." },
        { t: "Fresh smear made from well-mixed EDTA", flag: false, coach: "" },
        { t: "Tube filled to the line and inverted", flag: false, coach: "" },
        { t: "Sample labelled correctly", flag: false, coach: "" },
      ],
    },
    haem: {
      prompt: "Review the haematology and choose the best interpretation.",
      panel: [
        { a: "Platelets", v: "15", u: "×10⁹/L", ref: "200–500", flag: "low" },
        { a: "Platelet clumps on smear", v: "None", u: "", ref: "none", flag: null },
        { a: "PCV", v: "33", u: "%", ref: "37–55", flag: "low" },
        { a: "WBC", v: "11.0", u: "×10⁹/L", ref: "6.0–17.0", flag: null },
      ],
      options: [
        { t: "Confirmed severe thrombocytopenia (no clumping on the smear) with a mild secondary anaemia — consistent with immune-mediated thrombocytopenia.", score: 3, note: "A smear-confirmed platelet count of 15 with petechiae fits severe (often immune-mediated) thrombocytopenia." },
        { t: "An artefactually low count from clumping.", score: 0, note: "The smear shows NO clumps — the low count is real." },
        { t: "Normal platelets.", score: 0, note: "A count of 15 is severely low." },
        { t: "A regenerative anaemia from haemolysis.", score: 0, note: "The key abnormality is the platelet count, not haemolysis." },
      ],
    },
    biochem: {
      prompt: "A basic biochemistry is run to screen for organ involvement. Choose the best interpretation.",
      panel: [
        { a: "Albumin", v: "30", u: "g/L", ref: "26–40", flag: null },
        { a: "ALT", v: "70", u: "U/L", ref: "10–100", flag: null },
        { a: "Urea", v: "6.0", u: "mmol/L", ref: "3.5–9.0", flag: null },
        { a: "Total protein", v: "65", u: "g/L", ref: "54–75", flag: null },
      ],
      options: [
        { t: "Biochemistry is unremarkable — the problem is haematological (the platelets).", score: 3, note: "Correct: focus the report on the confirmed thrombocytopenia." },
        { t: "Liver disease.", score: 0, note: "Liver analytes are normal." },
        { t: "Renal disease.", score: 0, note: "Urea is normal." },
        { t: "Severe hypoproteinaemia.", score: 0, note: "Protein and albumin are within range." },
      ],
    },
    action: {
      prompt: "Best next step?",
      options: [
        { t: "Flag urgently to the vet, continue gentle handling, avoid unnecessary venepuncture, and prepare for further work-up (e.g. tick-borne disease, immune-mediated screen).", score: 3, note: "Severe true thrombocytopenia is an emergency; protect the patient and investigate the cause." },
        { t: "Flag the count to the vet.", score: 1, note: "Right, but emphasise the handling precautions and work-up." },
        { t: "Repeat off a jugular sample to be sure.", score: 0, note: "Avoid large-vessel puncture in a bleeding patient." },
        { t: "Send home and recheck in two weeks.", score: 0, note: "Spontaneous bleeding with a count of 15 needs urgent care." },
      ],
    },
  },
];
